/**
 * Focus workflow routes — full-page tasks with no primary sidebar or secondary panel.
 * Register product-agnostic path suffixes here; product prefixes (`/prism`, `/design-os`, …)
 * are stripped before matching.
 */

import { normalizeLibraryPathname } from "@/lib/library-nav"
import { isExamLockPath } from "@/lib/exam-lock-shell"
import { isRecordDetailChromePath } from "@/lib/record-detail-chrome"

/** Canonical suffixes (no product prefix). */
export const FOCUS_WORKFLOW_PATH_SUFFIXES = [
  "/library/new",
  "/focus-workflow",
] as const

const ASSESSMENT_QUESTION_CREATE_PATH =
  /^(\/course-offerings\/[^/]+)?\/assessments\/[^/]+\/questions\/new$/

/**
 * Import review. One batch, triaged and committed in a sitting, so the hub
 * chrome behind it is only somewhere to lose the thread.
 */
const QUESTION_IMPORT_REVIEW_PATH = /^\/question-bank\/import(\/[^/]+)?$/

const ASSESSMENT_ADD_QUESTIONS_PATH =
  /^(\/course-offerings\/[^/]+)?\/assessments\/[^/]+\/sections\/[^/]+\/add-questions$/

function trimTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname
}

/** Strip `/<product>` prefix so `/design-os/focus-workflow` → `/focus-workflow`. */
export function normalizeFocusWorkflowPath(pathname: string): string {
  const trimmed = trimTrailingSlash(pathname)
  const libraryNorm = normalizeLibraryPathname(trimmed)
  if (libraryNorm !== trimmed) return libraryNorm
  const withoutProduct = trimmed.replace(/^\/[^/]+(?=\/)/, "")
  return withoutProduct || trimmed
}

/** Add Questions keeps the primary sidebar hidden but owns a shell-level secondary rail. */
export function isAssessmentAddQuestionsPath(pathname: string): boolean {
  return ASSESSMENT_ADD_QUESTIONS_PATH.test(
    normalizeFocusWorkflowPath(pathname)
  )
}

/**
 * Add Questions assemble mode uses `ListPageTreePanelShell` on the canvas
 * (bank scopes + question list | details), same family as import review.
 * Compose (`?source=create`) and review (`?review=`) also own the full canvas.
 * The shell secondary rail is never claimed for this route.
 */
export function assessmentAddQuestionsOwnsSecondaryPanel(
  _pathname: string,
  _search: string
): boolean {
  return false
}

/** Whether the current route should hide primary + secondary sidebars. */
export function isSidebarHiddenPath(pathname: string): boolean {
  return (
    isFocusWorkflowPath(pathname) ||
    isExamLockPath(pathname) ||
    isRecordDetailChromePath(pathname)
  )
}

/** Whether the current route should hide all shell sidebars (primary + secondary). */
export function isFocusWorkflowPath(pathname: string): boolean {
  const normalized = normalizeFocusWorkflowPath(pathname)
  return (
    ASSESSMENT_QUESTION_CREATE_PATH.test(normalized) ||
    ASSESSMENT_ADD_QUESTIONS_PATH.test(normalized) ||
    QUESTION_IMPORT_REVIEW_PATH.test(normalized) ||
    FOCUS_WORKFLOW_PATH_SUFFIXES.some(
      (suffix) => normalized === suffix || normalized.startsWith(`${suffix}/`)
    )
  )
}
